import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import DocumentForm from "@/pages/DocumentForm";
import DocumentsList from "@/pages/DocumentsList";
import DocumentView from "@/pages/DocumentView";
import ParkedInvoices from "@/pages/ParkedInvoices";
import Customers from "@/pages/Customers";
import CustomersBySuburb from "@/pages/CustomersBySuburb";
import CustomerProfile from "@/pages/CustomerProfile";
import Catalogue from "@/pages/Catalogue";
import GoldRates from "@/pages/GoldRates";
import TermsSettings from "@/pages/TermsSettings";
import BranchSettings from "@/pages/BranchSettings";
import Reports from "@/pages/Reports";
import ActiveLoans from "@/pages/ActiveLoans";
import NewDocumentRoute from "@/pages/NewDocumentRoute";
import MyAccount from "@/pages/MyAccount";
import SetPassword from "@/pages/SetPassword";
import KuberIntegration from "@/pages/KuberIntegration";
import ResourceMonitor from "@/pages/ResourceMonitor";
import PaymentOptions from "@/pages/PaymentOptions";
import SocialLinks from "@/pages/SocialLinks";
import ReleaseNotes from "@/pages/ReleaseNotes";
import Guide from "@/pages/Guide";
import Calculations from "@/pages/Calculations";
import ExGstCalculator from "@/pages/ExGstCalculator";
import Discounts from "@/pages/Discounts";
import Privacy from "@/pages/Privacy";
import RateBoard from "@/pages/RateBoard";
import QueueWaits from "@/pages/QueueWaits";
import OldGoldReport from "@/pages/OldGoldReport";
import MoneyOwed from "@/pages/MoneyOwed";
import StaffTakings from "@/pages/StaffTakings";
import Repairs from "@/pages/Repairs";
import RepairForm from "@/pages/RepairForm";
import RepairView from "@/pages/RepairView";
import KycForm from "@/pages/KycForm";
import KycList from "@/pages/KycList";
import KycCollect from "@/pages/KycCollect";
import KuberPayments from "@/pages/KuberPayments";
import KycView from "@/pages/KycView";
import VisitLanding from "@/pages/VisitLanding";
import QueueDisplay from "@/pages/QueueDisplay";
import Queue from "@/pages/Queue";
import CashWatch from "@/pages/CashWatch";
import AuditLog from "@/pages/AuditLog";
import Users from "@/pages/Users";
import SignDocument from "@/pages/SignDocument";
import CustomerPortal from "@/pages/CustomerPortal";
import { CustomerSite } from "@/components/CustomerSite";
import { PreviewBanner } from "@/components/PreviewBanner";
import { RequirePermission } from "@/components/RequirePermission";
import { isCustomerHost, customerHost } from "@/lib/host";
import "@/App.css";

/** On the staff address the customer page is pushed over to the customer address
 *  once one is configured, so the two front doors never overlap. */
const CustomerEntry = () => {
  if (customerHost) {
    window.location.replace(`https://${customerHost}/invoices`);
    return null;
  }
  return <CustomerPortal />;
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null)
    return (
      <div
        className="flex h-screen items-center justify-center bg-[#0A0A0A] text-[#C5A059]"
        data-testid="auth-loading"
      >
        <span className="animate-pulse font-heading text-sm tracking-[0.3em]">
          DHUNNA
        </span>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && !window.location.pathname.startsWith("/my-account"))
    return <Navigate to="/my-account?first=1" replace />;
  return children;
};

function App() {
  if (isCustomerHost())
    return (
      <BrowserRouter>
        <PreviewBanner />
        <CustomerSite />
        <Toaster />
      </BrowserRouter>
    );

  return (
    <AuthProvider>
      <BrowserRouter>
        <PreviewBanner />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sign/:token" element={<SignDocument />} />
          <Route path="/invoices" element={<CustomerEntry />} />
          <Route path="/my-documents" element={<Navigate to="/invoices" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/display/:branch" element={<RateBoard />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/reset-password" element={<SetPassword />} />
          <Route path="/payment-options/:branch" element={<PaymentOptions />} />
          <Route path="/follow/:branch" element={<SocialLinks />} />
          <Route path="/kyc-form/:branch" element={<KycForm />} />
          <Route path="/visit/:branch" element={<VisitLanding />} />
          <Route path="/queue-display/:branch" element={<QueueDisplay />} />
          <Route path="/queue-display/:branch/:floor" element={<QueueDisplay />} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="new/:docType" element={<NewDocumentRoute />} />
            <Route path="guide" element={<Guide />} />
            <Route path="calculations" element={<Calculations />} />
            <Route path="calculator" element={<ExGstCalculator />} />
            <Route path="release-notes" element={<ReleaseNotes />} />
            <Route path="my-account" element={<MyAccount />} />
            <Route path="integrations/kuber" element={<KuberIntegration />} />
            <Route path="documents" element={<DocumentsList />} />
            <Route path="parked" element={<ParkedInvoices />} />
            <Route path="customers" element={<Customers />} />
            <Route path="customers/:phone" element={<CustomerProfile />} />
            <Route path="documents/:id" element={<DocumentView />} />
            <Route path="documents/:id/edit" element={<DocumentForm />} />
            <Route path="catalogue" element={<Catalogue />} />
            <Route path="gold-rates" element={<GoldRates />} />
            <Route path="terms" element={<TermsSettings />} />
            <Route path="stores" element={<BranchSettings />} />
            <Route path="reports" element={<RequirePermission perm="financials:read"><Reports /></RequirePermission>} />
            <Route path="reports/old-gold" element={<RequirePermission perm="financials:read"><OldGoldReport /></RequirePermission>} />
            <Route path="reports/money-owed" element={<RequirePermission perm="financials:read"><MoneyOwed /></RequirePermission>} />
            <Route path="reports/staff" element={<StaffTakings />} />
            <Route path="reports/queue-waits" element={<QueueWaits />} />
            <Route path="reports/discounts" element={<RequirePermission perm="audit:read"><Discounts /></RequirePermission>} />
            <Route path="reports/suburbs" element={<RequirePermission perm="customers:read"><CustomersBySuburb /></RequirePermission>} />
            <Route path="loans" element={<ActiveLoans />} />
            <Route path="repairs" element={<Repairs />} />
            <Route path="repairs/new" element={<RepairForm />} />
            <Route path="repairs/:id" element={<RepairView />} />
            <Route path="kyc" element={<KycList />} />
            <Route path="kyc/collect" element={<KycCollect />} />
            <Route path="kuber/payments" element={<KuberPayments />} />
            <Route path="kyc/:id" element={<KycView />} />
            <Route path="queue" element={<Queue />} />
            <Route path="reports/cash-watch" element={<CashWatch />} />
            <Route path="audit" element={<AuditLog />} />
            <Route path="monitoring" element={<ResourceMonitor />} />
            <Route path="users" element={<Users />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
